import { Component } from 'preact';
import { ACCENT_GOLD } from '../lib/medievalTheme';
import { getCrownLabel, getCrownTitle } from '../utils/arena/avatarCrowns';
import { getHallOfFameCached } from '../utils/arena/hallOfFameCache';
import { FOUNDER_TITLE_LABEL, FOUNDER_TITLE_EPITHET, FOUNDER_PICKER_SENTINEL } from '../utils/arena/founderTitle';
import { QA_TITLE_LABEL, QA_TITLE_EPITHET, QA_PICKER_SENTINEL } from '../utils/arena/qaTitle';

// Resolves and renders a player's currently-displayed crown title.
//
// Inputs (any one of these can provide the avatar id, in order):
//   1. `crown`. { avatarId, label, title, wins } already-resolved object
//   2. `player.displayedCrownAvatarId`. Server-stored explicit choice
//   3. (fallback) the player's highest-wins crown from the live Hall of
//      Fame snapshot. Only used when the player has no stored choice
//
// We always validate against fresh Hall of Fame data before rendering:
// if the stored avatar is no longer one the player holds the crown for,
// we silently hide the title rather than display a stale one. This is
// what lets the server store the user's choice without strict
// ownership validation.
//
// `variant` switches between the compact one-line form (used under
// usernames in lists and chips) and a two-line form (label on top,
// italic title underneath. Used in profile cards).
export default class PlayerTitle extends Component {
  state = { resolved: null };

  componentDidMount() {
    // Unconditionally notify the parent of the post-mount resolved
    // state, even when it's null. Without this initial fire, parents
    // that mirror title presence (e.g. the in-game HUD's trailing
    // comma) can't distinguish "still resolving" from "resolved to no
    // title" and end up showing stale state from a prior render.
    this.resolve().then((resolved) => {
      this.props.onResolve?.(!!resolved?.title, resolved?.title || null);
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.player?.id !== this.props.player?.id
      || prevProps.player?.displayedCrownAvatarId !== this.props.player?.displayedCrownAvatarId
      || prevProps.crown?.avatarId !== this.props.crown?.avatarId
    ) {
      this.resolve();
    }
    // Notify the parent any time the resolved title TEXT changes, not
    // just when presence flips. Covers both "title appeared / went
    // away" and "title swapped to a different crown" without leaving
    // mirror state stale. Second arg lets callers that need to
    // fit-to-width the title (e.g. the in-game HUD) pick a font size
    // from char count.
    const prevTitle = prevState.resolved?.title || null;
    const nowTitle = this.state.resolved?.title || null;
    if (prevTitle !== nowTitle) {
      this.props.onResolve?.(!!nowTitle, nowTitle);
    }
  }

  async resolve() {
    const resolved = await this.computeResolved();
    this.setState({ resolved });
    return resolved;
  }

  async computeResolved() {
    const { crown, player, sorceryCards } = this.props;
    if (crown?.avatarId) return crown;
    if (!player?.id) return null;
    // Founder title takes precedence in two cases:
    //   1. The player explicitly picked it from the title picker
    //      (displayedCrownAvatarId === FOUNDER_PICKER_SENTINEL).
    //   2. The player is a founder, holds no crowns, and hasn't
    //      explicitly hidden their title. Show the thank-you by
    //      default so founders without crowns see it without having
    //      to open Edit Profile.
    // We still validate `player.isFounder` because the sentinel could
    // be stale on a non-founder row after a server downgrade.
    const chosenId = player.displayedCrownAvatarId || null;
    if (player.isFounder && chosenId === FOUNDER_PICKER_SENTINEL) {
      return { founder: true, label: FOUNDER_TITLE_LABEL, title: FOUNDER_TITLE_EPITHET };
    }
    // The QA title behaves exactly like the founder one. Explicit
    // pick here, crownless default below. And wins the default over
    // it when a tester is also a founder, being the rarer of the two.
    if (player.isQaTester && chosenId === QA_PICKER_SENTINEL) {
      return { qa: true, label: QA_TITLE_LABEL, title: QA_TITLE_EPITHET };
    }
    try {
      const hof = await getHallOfFameCached();
      if (!hof?.avatars) return null;
      const heldRows = hof.avatars.filter((row) => row?.topPlayer?.id === player.id);
      // Role title with no crowns and no explicit pick → show it as
      // the default. Anyone who picked '__none__' gets nothing,
      // matching the existing opt-out semantics.
      if (heldRows.length === 0) {
        if (chosenId !== '__none__') {
          if (player.isQaTester) {
            return { qa: true, label: QA_TITLE_LABEL, title: QA_TITLE_EPITHET };
          }
          if (player.isFounder) {
            return { founder: true, label: FOUNDER_TITLE_LABEL, title: FOUNDER_TITLE_EPITHET };
          }
        }
        return null;
      }
      // Title display is a tri-state:
      //   null            → default; auto-pick the highest-wins crown
      //                     so any crown-holder gets a title without
      //                     having to first open Edit Profile.
      //   '__none__'      → player explicitly hid their title in the
      //                     Edit Profile picker. Render nothing.
      //   <avatarId>      → explicit pick; render that crown's title.
      if (chosenId === '__none__') return null;
      let row = chosenId ? heldRows.find((r) => r.avatarId === chosenId) : null;
      if (!row) {
        // No valid explicit pick. Fall back to the player's
        // highest-wins crown. This is the default state for any
        // crown-holder who hasn't visited Edit Profile yet.
        row = [...heldRows].sort((a, b) => (b.topPlayer.wins || 0) - (a.topPlayer.wins || 0))[0];
      }
      if (!row) return null;
      const meta = findAvatarMeta(sorceryCards, row.avatarId);
      const name = meta?.name || row.avatarId;
      return {
        avatarId: row.avatarId,
        avatarName: name,
        image: meta?.image || null,
        wins: row.topPlayer.wins || 0,
        label: getCrownLabel(name),
        title: getCrownTitle(name),
      };
    } catch (err) {
      console.warn('[PlayerTitle] Failed to resolve crown:', err);
      return null;
    }
  }

  render() {
    const { resolved } = this.state;
    const { variant = 'inline', className, style, prefix = ', ' } = this.props;
    if (!resolved?.title) return null;
    // The crown name ("Crown of Geomancy") is intentionally NOT
    // surfaced here. It lives next to the medallion itself on the
    // profile page where the visual + name read together as a unit.
    // Anywhere a player's name appears, only the italic epithet
    // ("Whose Foes Know Only Shock") goes after it.
    //
    // The inline variant renders as `, <Title>` so it reads as a
    // medieval byline ("Aragorn, son of Arathorn"). Drop it next to
    // the player name in the same line and the comma stitches them
    // together visually. Pass prefix="" to suppress the comma when
    // the surrounding markup already provides one.
    if (variant === 'block') {
      return (
        <div className={className} style={style}>
          <div className="text-[0.6875cqw] italic" style={{ color: ACCENT_GOLD, opacity: 0.9 }}>
            {resolved.title}
          </div>
        </div>
      );
    }
    return (
      <span
        className={`text-[0.625cqw] italic truncate ${className || ''}`}
        style={{ color: ACCENT_GOLD, opacity: 0.9, ...style }}
        title={resolved.label}
      >
        {prefix}{resolved.title}
      </span>
    );
  }
}

function findAvatarMeta(sorceryCards, avatarId) {
  if (!Array.isArray(sorceryCards)) return null;
  for (const c of sorceryCards) {
    if (c.type !== 'Avatar') continue;
    if (c.unique_id === avatarId) {
      return {
        id: c.unique_id,
        name: c.name || c.unique_id,
        image: c.printings?.[0]?.image_url || null,
      };
    }
  }
  return null;
}
